//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect } from 'react';
import { documentationStyles } from '../../styles/Documentation';
import News from '../../icons/News.svg';
import Video from '../../icons/Video.svg';
import Blog from '../../icons/Blog.svg';
import Help from '../../icons/Help.svg';
import NewsSelected from '../../icons/NewsBlue.svg';
import VideoSelected from '../../icons/VideoBlue.svg';
import BlogSelected from '../../icons/BlogBlue.svg';
import HelpSelected from '../../icons/HelpBlue.svg';
import Expand from '../../icons/ExpandBlue.svg';
import Collapse from '../../icons/CollapseBlue.svg';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../Config';
import settings from '../../Config/settings';

type Provider = keyof typeof settings.links;

interface RightSidebarProps {
  zoom: number;
  leftHeight: number;
  onExpandChange?: (expanded: boolean) => void;
}

const RightSidebar: React.FC<RightSidebarProps> = ({
  zoom,
  leftHeight,
  onExpandChange,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const { t } = useTranslation();
  const { services } = useConfig();
  const provider = (services?.provider?.name || '') as Provider;
  const providerLinks = settings.links[provider];

  useEffect(() => {
    if (onExpandChange) onExpandChange(expandedIndex !== null);
  }, [expandedIndex, onExpandChange]);

  const scaledCollapsedWidth =
    documentationStyles.rightSidebar.collapsedWidth / zoom;
  const scaledExpandedWidth = documentationStyles.rightSidebar.baseWidth / zoom;
  const scaledPadding = Math.round(
    documentationStyles.rightSidebar.basePadding / zoom,
  );
  const scaledFontSize = documentationStyles.rightSidebar.baseFontSize / zoom;

  const getLoopBlogs = (provider: Provider) => {
    if (provider === 'HCL') {
      return [
        {
          tag: '',
          heading: t('documentation-hcl-blog-heading'),
          link: 'https://www.hcl-software.com/blog/secure-devops/introducing-hcl-devops-loop-smarter-insights-unified-software-delivery',
          brief: t('documentation-hcl-blog-description'),
        },
      ];
    } else if (provider === 'IBM') {
      return [
        {
          tag: '',
          heading: t('documentation-ibm-blog-heading-1'),
          link: 'https://community.ibm.com/community/user/blogs/cassidy-rimer/2025/06/30/introducing-the-new-devops-loop-smarter-insights-u',
          brief: t('documentation-ibm-blog-description-1'),
        },
        {
          tag: '',
          heading: t('documentation-ibm-blog-heading-2'),
          link: 'https://community.ibm.com/community/user/blogs/randy-langehennig1/2025/08/23/devops-loop-a-first-look-at-the-new-integrated-pla',
          brief: t('documentation-ibm-blog-description-2'),
        },
      ];
    } else {
      return [];
    }
  };

  const icons = [
    {
      id: 0,
      label: t('devops-loop-news'),
      Tag: t('news'),
      icon: News,
      selectedIcon: NewsSelected,
      news: [
        {
          tag: '',
          heading: t('whats-new-in-v2-0-0'),
          link: providerLinks.news,
          brief: t('whats-new-in-v2-0-0-description'),
        },
      ],
    },
    {
      id: 1,
      label: t('devops-loop-videos'),
      Tag: t('videos'),
      icon: Video,
      selectedIcon: VideoSelected,
      news: [
        {
          tag: t('teamspace'),
          heading: t('creating-teamspace'),
          link: providerLinks.video.teamspaceCreation,
          brief: t('teamspace-creation-video-description'),
        },
        {
          tag: t('loop'),
          heading: t('creating-loop'),
          link: providerLinks.video.loopCreation,
          brief: t('loop-creation-video-description'),
        },
      ],
    },
    {
      id: 2,
      label: t('devops-loop-blogs'),
      Tag: t('blogs'),
      icon: Blog,
      selectedIcon: BlogSelected,
      news: getLoopBlogs(provider),
    },
    {
      id: 3,
      label: t('devops-loop-help'),
      Tag: t('help'),
      icon: Help,
      selectedIcon: HelpSelected,
      news: [
        {
          tag: t('guide'),
          heading: t('user-manual'),
          link: providerLinks.userManual,
          brief: t('user-manual-description'),
        },
      ],
    },
  ];

  // Sliding effect styles
  const sidebarHeight = leftHeight ? `${leftHeight - 1}px` : 'auto';

  return (
    <div
      id="id-docs-right-container"
      data-testid="docs-right-container"
      style={{
        height: sidebarHeight,
        width:
          expandedIndex !== null
            ? scaledExpandedWidth + scaledCollapsedWidth
            : scaledCollapsedWidth,
        transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        marginTop: `${documentationStyles.rightSidebar.marginTop / zoom}px`,
        boxShadow:
          expandedIndex !== null
            ? documentationStyles.rightSidebar.boxShadow
            : undefined,
        background: '#fff',
      }}
    >
      {/* Info panel slides in from the right, sits to the left of icons */}
      <div
        id="id-docs-right-info-panel"
        data-testid="docs-right-info-panel"
        style={{
          width: expandedIndex !== null ? scaledExpandedWidth : 0,
          minWidth: 0,
          maxWidth: scaledExpandedWidth,
          overflow: 'hidden',
          background: '#fff',
          transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
          boxShadow:
            expandedIndex !== null
              ? documentationStyles.rightSidebar.boxShadow
              : undefined,
        }}
      >
        {expandedIndex !== null && (
          <div
            id="id-docs-right-news-container"
            data-testid="docs-right-news-container"
            style={{
              ...documentationStyles.rightSidebar.newsContainer,
              padding: `${scaledPadding}px`,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <span
              id="id-docs-right-expanded-label"
              data-testid="docs-right-expanded-label"
              style={{
                ...documentationStyles.rightSidebar.expandedLabel,
                fontSize: `${scaledFontSize}px`,
                cursor: 'pointer',
                display: 'block',
                marginBottom: `${scaledPadding}px`,
              }}
              onClick={() => setExpandedIndex(null)}
            >
              {icons[expandedIndex].label}
            </span>
            {icons[expandedIndex].news.map((item, idx) => (
              <div
                key={idx}
                id={`id-docs-right-news-item-${idx}`}
                data-testid={`docs-right-news-item-${idx}`}
                style={{
                  ...documentationStyles.rightSidebar.newsItem,
                  marginBottom: `${scaledPadding}px`,
                  paddingBottom: `${scaledPadding / 2}px`,
                  borderBottom: '1px solid #eee',
                }}
              >
                <div
                  id={`id-docs-right-news-tag-${idx}`}
                  data-testid={`docs-right-news-tag-${idx}`}
                  style={{
                    ...documentationStyles.rightSidebar.newsTag,
                    fontSize: `${12 / zoom}px`,
                  }}
                >
                  {item.tag}
                </div>
                <div
                  id={`id-docs-right-news-heading-${idx}`}
                  data-testid={`docs-right-news-heading-${idx}`}
                  style={{
                    ...documentationStyles.rightSidebar.newsHeading,
                    fontSize: `${15 / zoom}px`,
                    fontWeight: 600,
                    margin: '2px 0',
                  }}
                >
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...documentationStyles.rightSidebar.newsLink,
                      color: '#0056b3',
                      textDecoration: 'underline',
                    }}
                  >
                    {item.heading}
                  </a>
                </div>
                <div
                  id={`id-docs-right-news-brief-${idx}`}
                  data-testid={`docs-right-news-brief-${idx}`}
                  style={{
                    ...documentationStyles.rightSidebar.newsBrief,
                    fontSize: `${13 / zoom}px`,
                    color: '#444',
                  }}
                >
                  {item.brief}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Collapsed icons always visible on the right, now aligned to top */}
      <div
        id="id-docs-right-icons-panel"
        data-testid="docs-right-icons-panel"
        style={{
          width: scaledCollapsedWidth,
          height: '100%',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start', // align icons to top
          gap: `${scaledPadding}px`,
          zIndex: 3,
          boxShadow:
            expandedIndex !== null ? '0 0 8px rgba(0,0,0,0.08)' : undefined,
        }}
      >
        {icons.map((icon, idx) => (
          <div
            key={icon.id}
            id={`id-docs-right-icon-item-${icon.id}`}
            data-testid={`docs-right-icon-item-${icon.id}`}
            style={{
              ...documentationStyles.rightSidebar.iconItem,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            title={icon.label}
            onClick={() => {
              if (expandedIndex === idx) {
                setExpandedIndex(null);
              } else {
                setExpandedIndex(idx);
              }
            }}
          >
            <span
              id={`id-docs-right-icon-label-${icon.id}`}
              data-testid={`docs-right-icon-label-${icon.id}`}
              style={{
                ...documentationStyles.rightSidebar.iconLabel,
                fontSize: `${scaledFontSize}px`,
                display: 'block',
              }}
            >
              <img
                src={expandedIndex === idx ? icon.selectedIcon : icon.icon}
                alt={icon.label}
                style={
                  expandedIndex === idx
                    ? {
                        width: 23,
                        height: 23,
                        borderRadius: 4,
                        transition: 'width 0.2s, height 0.2s',
                      }
                    : {
                        width: scaledFontSize,
                        height: scaledFontSize,
                        transition: 'width 0.2s, height 0.2s',
                      }
                }
              />
            </span>
            <span
              id={`id-docs-right-icon-tag-${icon.id}`}
              data-testid={`docs-right-icon-tag-${icon.id}`}
              style={{
                ...documentationStyles.rightSidebar.iconTag,
                fontSize: `${scaledFontSize * 0.7}px`,
                maxWidth: scaledCollapsedWidth - 16,
                textAlign: 'center',
                whiteSpace: 'normal',
              }}
            >
              {icon.Tag}
            </span>
          </div>
        ))}
        {/* Expand/Collapse icons at the bottom */}
        <div
          id="id-docs-right-expand-collapse"
          data-testid="docs-right-expand-collapse"
          style={{
            marginTop: 'auto',
            marginBottom: `${scaledPadding}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {expandedIndex === null ? (
            <img
              src={Expand}
              alt="Expand"
              title="Expand sidebar"
              style={{
                width: 23,
                height: 23,
                marginBottom: 4,
                transition: 'width 0.2s, height 0.2s',
              }}
              onClick={() => setExpandedIndex(0)}
            />
          ) : (
            <img
              src={Collapse}
              alt="Collapse"
              title="Collapse sidebar"
              style={{
                width: 23,
                height: 23,
                marginBottom: 4,
                transition: 'width 0.2s, height 0.2s',
              }}
              onClick={() => setExpandedIndex(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RightSidebar;
